/**
 * NATURAH THEME - Quick Add Functionality
 * Handles quick add to cart with loading, success, and error states
 * File: assets/quick-add.js
 * Version: 1.0.0
 */

class QuickAdd {
  constructor() {
    this.buttons = document.querySelectorAll('[data-quick-add-btn]');
    this.init();
  }

  init() {
    this.buttons.forEach(button => {
      button.addEventListener('click', (e) => this.handleQuickAdd(e));
    });
  }

  async handleQuickAdd(e) {
    e.preventDefault();
    e.stopPropagation(); // Prevent card link navigation

    const button = e.currentTarget;
    const variantId = button.dataset.variantId;

    if (!variantId || button.disabled) {
      return;
    }

    // Get UI elements
    const textEl = button.querySelector('[data-quick-add-text]');
    const loadingEl = button.querySelector('[data-loading]');
    const successIcon = button.querySelector('[data-success-icon]');

    // Set loading state
    this.setLoadingState(button, textEl, loadingEl, true);

    // Optimistic: update badge instantly before network request
    window.NaturahCart.optimisticAdd(1);

    try {
      // Add item to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const data = await response.json();

      // Success state
      this.setSuccessState(button, textEl, loadingEl, successIcon);

      // Reconcile with actual server count (corrects any drift)
      window.NaturahCart.reconcile();

      // Trigger custom event for cart drawer or other components
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { variant: data, quantity: 1 }
      }));

      // Reset button after 2 seconds
      setTimeout(() => {
        this.resetButton(button, textEl, loadingEl, successIcon);
      }, 2000);

    } catch (error) {
      console.error('Quick add error:', error);

      // Revert optimistic update on failure
      window.NaturahCart.revert(1);

      // Error state
      this.setErrorState(button, textEl, loadingEl, error.message);

      // Reset button after 3 seconds
      setTimeout(() => {
        this.resetButton(button, textEl, loadingEl, successIcon);
      }, 3000);
    }
  }

  setLoadingState(button, textEl, loadingEl, isLoading) {
    button.disabled = isLoading;
    button.classList.add('btn--loading');

    if (textEl) {
      textEl.hidden = isLoading;
    }

    if (loadingEl) {
      loadingEl.hidden = !isLoading;
      loadingEl.setAttribute('aria-hidden', !isLoading);
    }
  }

  setSuccessState(button, textEl, loadingEl, successIcon) {
    button.disabled = false;
    button.classList.remove('btn--loading');
    button.classList.add('btn--success');

    if (loadingEl) {
      loadingEl.hidden = true;
      loadingEl.setAttribute('aria-hidden', 'true');
    }

    if (successIcon) {
      successIcon.hidden = false;
      successIcon.setAttribute('aria-hidden', 'false');
    }

    if (textEl) {
      textEl.textContent = 'Added!';
      textEl.hidden = false;
    }

    // Announce to screen readers
    this.announceToScreenReader('Item added to cart successfully');
  }

  setErrorState(button, textEl, loadingEl, errorMessage) {
    button.disabled = false;
    button.classList.remove('btn--loading');
    button.classList.add('btn--error');

    if (loadingEl) {
      loadingEl.hidden = true;
      loadingEl.setAttribute('aria-hidden', 'true');
    }

    if (textEl) {
      textEl.textContent = 'Error';
      textEl.hidden = false;
    }

    // Announce error to screen readers
    this.announceToScreenReader(`Error adding to cart: ${errorMessage}`);
  }

  resetButton(button, textEl, loadingEl, successIcon) {
    button.disabled = false;
    button.classList.remove('btn--loading', 'btn--success', 'btn--error');

    if (loadingEl) {
      loadingEl.hidden = true;
      loadingEl.setAttribute('aria-hidden', 'true');
    }

    if (successIcon) {
      successIcon.hidden = true;
      successIcon.setAttribute('aria-hidden', 'true');
    }

    if (textEl) {
      textEl.textContent = 'Quick Add';
      textEl.hidden = false;
    }
  }

  async updateCartCount() {
    // Delegate to shared cart utility (also triggered by cart:item-added event)
    return window.NaturahCart.updateCartCount();
  }

  announceToScreenReader(message) {
    // Create or use existing live region for announcements
    let liveRegion = document.querySelector('[data-quick-add-announcer]');

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('data-quick-add-announcer', '');
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }

    // Clear and set new message
    liveRegion.textContent = '';
    setTimeout(() => {
      liveRegion.textContent = message;
    }, 100);
  }
}

// Shared cart utility — used by quick-add, product page, and any future add-to-cart paths
// Uses optimistic updates: badge increments instantly, then reconciles with server
window.NaturahCart = {
  // Optimistic update — call BEFORE the /cart/add.js request for instant feedback
  optimisticAdd(quantity) {
    quantity = quantity || 1;
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(el => {
      const current = parseInt(el.getAttribute('data-cart-count')) || 0;
      const next = current + quantity;

      el.textContent = next;
      el.setAttribute('data-cart-count', next);

      if (el.hidden) {
        el.hidden = false;
      }

      // Instant animation feedback
      el.classList.remove('cart-count--updated');
      void el.offsetWidth;
      el.classList.add('cart-count--updated');

      setTimeout(() => {
        el.classList.remove('cart-count--updated');
      }, 350);
    });
  },

  // Reconcile with server — call AFTER /cart/add.js succeeds to correct any drift
  async reconcile() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(el => {
        const displayed = parseInt(el.getAttribute('data-cart-count')) || 0;
        if (displayed !== cart.item_count) {
          el.textContent = cart.item_count;
          el.setAttribute('data-cart-count', cart.item_count);
        }
      });
    } catch (error) {
      console.error('Failed to reconcile cart count:', error);
    }
  },

  // Revert optimistic update on failure
  revert(quantity) {
    quantity = quantity || 1;
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(el => {
      const current = parseInt(el.getAttribute('data-cart-count')) || 0;
      const reverted = Math.max(0, current - quantity);

      el.textContent = reverted;
      el.setAttribute('data-cart-count', reverted);

      if (reverted === 0) {
        el.hidden = true;
      }
    });
  },

  // Legacy method — kept for backwards compat, now just reconciles
  async updateCartCount() {
    return this.reconcile();
  }
};

// Listen for cart:item-added from external add-to-cart paths (e.g., cart drawer, apps)
// Internal paths (quick-add, product-page) handle optimistic updates directly
document.addEventListener('cart:item-added', () => {
  window.NaturahCart.reconcile();
});

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new QuickAdd());
} else {
  new QuickAdd();
}

// Export for use by other scripts (e.g., product recommendations)
window.QuickAdd = QuickAdd;
